<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { picoSearch } from '@scmmishra/pico-search';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useTrack } from 'dashboard/composables';
import { useConversationFilterContext } from './provider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import LabelTagIcon from 'dashboard/components-next/label/LabelTagIcon.vue';
import Button from 'next/button/Button.vue';

const emit = defineEmits(['applyFilter', 'close']);

const { filterTypes } = useConversationFilterContext();
const { t } = useI18n();
const store = useStore();
const currentRole = useMapGetter('getCurrentRole');
const rawLabels = useMapGetter('labels/getLabels');

const isManager = computed(() => currentRole.value === 'administrator');

const statusOptions = computed(
  () => filterTypes.value.find(f => f.attributeKey === 'status')?.options ?? []
);
const priorityOptions = computed(
  () => filterTypes.value.find(f => f.attributeKey === 'priority')?.options ?? []
);
const inboxOptions = computed(
  () => filterTypes.value.find(f => f.attributeKey === 'inbox_id')?.options ?? []
);
const agentOptions = computed(
  () => filterTypes.value.find(f => f.attributeKey === 'assignee_id')?.options ?? []
);
const teamOptions = computed(
  () => filterTypes.value.find(f => f.attributeKey === 'team_id')?.options ?? []
);
const labelOptions = computed(() =>
  rawLabels.value.map(l => ({ id: l.title, name: l.title, color: l.color }))
);

const selStatus = ref([]);
const selPriority = ref([]);
const selInboxes = ref([]);
const selLabels = ref([]);
const selAgents = ref([]);
const selTeams = ref([]);
const searchAgent = ref('');
const searchInbox = ref('');

const filteredAgents = computed(() =>
  searchAgent.value
    ? picoSearch(agentOptions.value, searchAgent.value, ['name'])
    : agentOptions.value
);
const filteredInboxes = computed(() =>
  searchInbox.value
    ? picoSearch(inboxOptions.value, searchInbox.value, ['name'])
    : inboxOptions.value
);

const openSection = ref(null);
const toggleSection = key => {
  openSection.value = openSection.value === key ? null : key;
};
// Aceleres: colapsa qualquer seção (menu) aberta ANTES de emitir 'close'.
// Sem isso, ao fechar o painel de filtros com uma seção aberta, o menu e o
// scroll dela continuam visíveis durante o fechamento do container.
const close = () => {
  openSection.value = null;
  emit('close');
};

const toggle = (arr, id) => {
  const idx = arr.indexOf(id);
  if (idx === -1) arr.push(id);
  else arr.splice(idx, 1);
};
const isSelected = (arr, id) => arr.includes(id);

const hasSelections = computed(
  () =>
    selStatus.value.length > 0 ||
    selPriority.value.length > 0 ||
    selInboxes.value.length > 0 ||
    selLabels.value.length > 0 ||
    selAgents.value.length > 0 ||
    selTeams.value.length > 0
);

const clearFilters = () => {
  selStatus.value = [];
  selPriority.value = [];
  selInboxes.value = [];
  selLabels.value = [];
  selAgents.value = [];
  selTeams.value = [];
  searchAgent.value = '';
  searchInbox.value = '';
};

const buildConditions = () => {
  const conditions = [];

  if (selStatus.value.length) {
    conditions.push({
      attributeKey: 'status',
      filterOperator: 'equal_to',
      values: selStatus.value.map(id => ({
        id,
        name: statusOptions.value.find(s => s.id === id)?.name ?? id,
      })),
      queryOperator: 'and',
    });
  }

  if (selPriority.value.length) {
    conditions.push({
      attributeKey: 'priority',
      filterOperator: 'equal_to',
      values: selPriority.value.map(id => ({
        id,
        name: priorityOptions.value.find(p => p.id === id)?.name ?? id,
      })),
      queryOperator: 'and',
    });
  }

  selInboxes.value.forEach((inboxId, i) => {
    const inbox = inboxOptions.value.find(x => x.id === inboxId);
    conditions.push({
      attributeKey: 'inbox_id',
      filterOperator: 'equal_to',
      values: { id: inboxId, name: inbox ? inbox.name : String(inboxId) },
      queryOperator: i === 0 && conditions.length === 0 ? 'and' : 'or',
    });
  });

  if (selLabels.value.length) {
    conditions.push({
      attributeKey: 'labels',
      filterOperator: 'equal_to',
      values: selLabels.value.map(id => ({ id, name: id })),
      queryOperator: 'and',
    });
  }

  selAgents.value.forEach((agentId, i) => {
    const agent = agentOptions.value.find(a => a.id === agentId);
    conditions.push({
      attributeKey: 'assignee_id',
      filterOperator: 'equal_to',
      values: { id: agentId, name: agent ? agent.name : String(agentId) },
      queryOperator:
        i === 0 &&
        !selInboxes.value.length &&
        !selLabels.value.length &&
        !selStatus.value.length &&
        !selPriority.value.length
          ? 'and'
          : 'or',
    });
  });

  selTeams.value.forEach((teamId, i) => {
    const team = teamOptions.value.find(t => t.id === teamId);
    conditions.push({
      attributeKey: 'team_id',
      filterOperator: 'equal_to',
      values: { id: teamId, name: team ? team.name : String(teamId) },
      queryOperator: i === 0 ? 'and' : 'or',
    });
  });

  return conditions;
};

const applyFilters = () => {
  const conditions = buildConditions();
  if (!conditions.length) return;
  store.dispatch(
    'setConversationFilters',
    useSnakeCase(JSON.parse(JSON.stringify(conditions)))
  );
  emit('applyFilter', conditions);
  useTrack(CONVERSATION_EVENTS.APPLY_FILTER, {
    appliedFilters: conditions.map(f => ({
      key: f.attributeKey,
      operator: f.filterOperator,
      queryOperator: f.queryOperator,
    })),
  });
};

const outsideClickHandler = [
  close,
  { ignore: ['#toggleConversationFilterButton'] },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="acl-conversation-filter-panel z-40 w-72 border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl overflow-hidden flex flex-col"
    style="max-height: min(540px, 85vh)"
  >
    <div class="flex items-center justify-between px-4 py-3 border-b border-n-weak shrink-0">
      <div class="flex items-center gap-2">
        <span class="i-lucide-sliders-horizontal size-3.5 text-n-slate-10" />
        <h3 class="text-sm font-semibold text-n-slate-12">Filtros</h3>
        <span
          v-if="hasSelections"
          class="px-1.5 py-0.5 bg-n-blue-9 text-white text-[10px] font-semibold rounded-full"
        >
          {{ selStatus.length + selPriority.length + selInboxes.length + selLabels.length + selAgents.length + selTeams.length }}
        </span>
      </div>
      <button
        class="text-n-slate-10 hover:text-n-slate-12 transition-colors p-1 rounded-md hover:bg-n-alpha-2"
        @click="close"
      >
        <span class="i-lucide-x size-3.5" />
      </button>
    </div>

    <div class="flex-1 overflow-y-auto scrollbar-thin divide-y divide-n-weak/50 min-h-0">

      <div v-if="isManager && inboxOptions.length">
        <button
          class="w-full flex items-center justify-between px-4 py-2.5 hover:bg-n-alpha-1 transition-colors"
          @click="toggleSection('canais')"
        >
          <div class="flex items-center gap-2.5">
            <span class="i-lucide-inbox size-3.5 text-n-slate-9" />
            <span class="text-xs font-medium text-n-slate-12">Canais</span>
          </div>
          <div class="flex items-center gap-1.5">
            <span v-if="selInboxes.length" class="px-1.5 py-0.5 bg-n-blue-9 text-white text-[10px] font-bold rounded-full min-w-[18px] text-center">{{ selInboxes.length }}</span>
            <span class="size-3.5 text-n-slate-9 transition-transform" :class="openSection === 'canais' ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" />
          </div>
        </button>
        <div v-if="openSection === 'canais'" class="px-3 pb-3">
          <div class="relative mb-2">
            <span class="i-lucide-search absolute left-2.5 top-1/2 -translate-y-1/2 size-3 text-n-slate-9" />
            <input v-model="searchInbox" class="w-full pl-7 pr-2 py-1.5 text-xs bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-11 focus:outline-none focus:border-n-blue-8" placeholder="Buscar canal..." />
          </div>
          <div class="space-y-0.5 max-h-36 overflow-y-auto">
            <button
              v-for="inbox in filteredInboxes"
              :key="inbox.id"
              class="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all"
              :class="isSelected(selInboxes, inbox.id) ? 'bg-n-blue-3 text-n-blue-11' : 'text-n-slate-11 hover:bg-n-alpha-2'"
              @click="toggle(selInboxes, inbox.id)"
            >
              <span class="flex-1 text-left truncate">{{ inbox.name }}</span>
              <span v-if="isSelected(selInboxes, inbox.id)" class="i-lucide-check size-3 shrink-0" />
            </button>
          </div>
        </div>
      </div>

      <div v-if="labelOptions.length">
        <button
          class="w-full flex items-center justify-between px-4 py-2.5 hover:bg-n-alpha-1 transition-colors"
          @click="toggleSection('etiquetas')"
        >
          <div class="flex items-center gap-2.5">
            <LabelTagIcon :size="14" class="text-n-slate-9" />
            <span class="text-xs font-medium text-n-slate-12">Etiquetas</span>
          </div>
          <div class="flex items-center gap-1.5">
            <span v-if="selLabels.length" class="px-1.5 py-0.5 bg-n-blue-9 text-white text-[10px] font-bold rounded-full min-w-[18px] text-center">{{ selLabels.length }}</span>
            <span class="size-3.5 text-n-slate-9 transition-transform" :class="openSection === 'etiquetas' ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" />
          </div>
        </button>
        <div v-if="openSection === 'etiquetas'" class="px-3 pb-3">
          <div class="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
            <button
              v-for="label in labelOptions"
              :key="label.id"
              class="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all"
              :class="isSelected(selLabels, label.id) ? 'bg-n-blue-3 border-n-blue-7 text-n-blue-11' : 'bg-n-alpha-1 border-n-weak text-n-slate-11 hover:border-n-slate-6'"
              @click="toggle(selLabels, label.id)"
            >
              <LabelTagIcon :color="label.color" :size="14" />
              {{ label.name }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="agentOptions.length">
        <button
          class="w-full flex items-center justify-between px-4 py-2.5 hover:bg-n-alpha-1 transition-colors"
          @click="toggleSection('usuarios')"
        >
          <div class="flex items-center gap-2.5">
            <span class="i-lucide-users size-3.5 text-n-slate-9" />
            <span class="text-xs font-medium text-n-slate-12">Usuários</span>
          </div>
          <div class="flex items-center gap-1.5">
            <span v-if="selAgents.length" class="px-1.5 py-0.5 bg-n-blue-9 text-white text-[10px] font-bold rounded-full min-w-[18px] text-center">{{ selAgents.length }}</span>
            <span class="size-3.5 text-n-slate-9 transition-transform" :class="openSection === 'usuarios' ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" />
          </div>
        </button>
        <div v-if="openSection === 'usuarios'" class="px-3 pb-3">
          <div class="relative mb-2">
            <span class="i-lucide-search absolute left-2.5 top-1/2 -translate-y-1/2 size-3 text-n-slate-9" />
            <input v-model="searchAgent" class="w-full pl-7 pr-2 py-1.5 text-xs bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-11 focus:outline-none focus:border-n-blue-8" placeholder="Buscar usuário..." />
          </div>
          <div class="space-y-0.5 max-h-36 overflow-y-auto">
            <button
              v-for="agent in filteredAgents"
              :key="agent.id"
              class="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all"
              :class="isSelected(selAgents, agent.id) ? 'bg-n-blue-3 text-n-blue-11' : 'text-n-slate-11 hover:bg-n-alpha-2'"
              @click="toggle(selAgents, agent.id)"
            >
              <span class="w-5 h-5 rounded-full bg-n-slate-4 flex items-center justify-center text-[10px] font-semibold text-n-slate-11 shrink-0 uppercase">{{ agent.name ? agent.name.charAt(0) : '?' }}</span>
              <span class="flex-1 text-left truncate">{{ agent.name }}</span>
              <span v-if="isSelected(selAgents, agent.id)" class="i-lucide-check size-3 shrink-0" />
            </button>
          </div>
        </div>
      </div>

      <div v-if="teamOptions.length">
        <button
          class="w-full flex items-center justify-between px-4 py-2.5 hover:bg-n-alpha-1 transition-colors"
          @click="toggleSection('equipes')"
        >
          <div class="flex items-center gap-2.5">
            <span class="i-lucide-building-2 size-3.5 text-n-slate-9" />
            <span class="text-xs font-medium text-n-slate-12">Equipes</span>
          </div>
          <div class="flex items-center gap-1.5">
            <span v-if="selTeams.length" class="px-1.5 py-0.5 bg-n-blue-9 text-white text-[10px] font-bold rounded-full min-w-[18px] text-center">{{ selTeams.length }}</span>
            <span class="size-3.5 text-n-slate-9 transition-transform" :class="openSection === 'equipes' ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" />
          </div>
        </button>
        <div v-if="openSection === 'equipes'" class="px-3 pb-3">
          <div class="space-y-0.5 max-h-36 overflow-y-auto">
            <button
              v-for="team in teamOptions"
              :key="team.id"
              class="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all"
              :class="isSelected(selTeams, team.id) ? 'bg-n-blue-3 text-n-blue-11' : 'text-n-slate-11 hover:bg-n-alpha-2'"
              @click="toggle(selTeams, team.id)"
            >
              <span class="flex-1 text-left truncate">{{ team.name }}</span>
              <span v-if="isSelected(selTeams, team.id)" class="i-lucide-check size-3 shrink-0" />
            </button>
          </div>
        </div>
      </div>

      <div>
        <button
          class="w-full flex items-center justify-between px-4 py-2.5 hover:bg-n-alpha-1 transition-colors"
          @click="toggleSection('status')"
        >
          <div class="flex items-center gap-2.5">
            <span class="i-lucide-circle-dot size-3.5 text-n-slate-9" />
            <span class="text-xs font-medium text-n-slate-12">Status</span>
          </div>
          <div class="flex items-center gap-1.5">
            <span v-if="selStatus.length" class="px-1.5 py-0.5 bg-n-blue-9 text-white text-[10px] font-bold rounded-full min-w-[18px] text-center">{{ selStatus.length }}</span>
            <span class="size-3.5 text-n-slate-9 transition-transform" :class="openSection === 'status' ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" />
          </div>
        </button>
        <div v-if="openSection === 'status'" class="px-3 pb-3">
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="status in statusOptions"
              :key="status.id"
              class="px-2.5 py-1 rounded-full text-xs font-medium border transition-all"
              :class="isSelected(selStatus, status.id) ? 'bg-n-blue-9 border-n-blue-9 text-white' : 'bg-n-alpha-1 border-n-weak text-n-slate-11 hover:border-n-slate-6'"
              @click="toggle(selStatus, status.id)"
            >
              {{ status.name }}
            </button>
          </div>
        </div>
      </div>

      <div>
        <button
          class="w-full flex items-center justify-between px-4 py-2.5 hover:bg-n-alpha-1 transition-colors"
          @click="toggleSection('prioridade')"
        >
          <div class="flex items-center gap-2.5">
            <span class="i-lucide-flag size-3.5 text-n-slate-9" />
            <span class="text-xs font-medium text-n-slate-12">Prioridade</span>
          </div>
          <div class="flex items-center gap-1.5">
            <span v-if="selPriority.length" class="px-1.5 py-0.5 bg-n-blue-9 text-white text-[10px] font-bold rounded-full min-w-[18px] text-center">{{ selPriority.length }}</span>
            <span class="size-3.5 text-n-slate-9 transition-transform" :class="openSection === 'prioridade' ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" />
          </div>
        </button>
        <div v-if="openSection === 'prioridade'" class="px-3 pb-3">
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="priority in priorityOptions"
              :key="priority.id"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all"
              :class="isSelected(selPriority, priority.id) ? 'bg-n-blue-9 border-n-blue-9 text-white' : 'bg-n-alpha-1 border-n-weak text-n-slate-11 hover:border-n-slate-6'"
              @click="toggle(selPriority, priority.id)"
            >
              <CardPriorityIcon :priority="priority.id" />
              <span>{{ priority.name }}</span>
            </button>
          </div>
        </div>
      </div>

    </div>

    <div class="flex gap-2 px-4 py-3 border-t border-n-weak shrink-0">
      <Button sm faded slate class="flex-1" :disabled="!hasSelections" @click="clearFilters">
        Limpar filtros
      </Button>
      <Button sm solid blue class="flex-1" :disabled="!hasSelections" @click="applyFilters">
        Aplicar
      </Button>
    </div>
  </div>
</template>
